import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { IconButton as MuiIconButton } from '@mui/material';
import AppToolTip from '../InputBox/AppToolTip';
import { sizes } from '../../Tokens/Sizes';
import { borderRadius, layoutSpacing } from '../../Tokens/Scales';

const resolveIcon = (icon) => {
  if (!icon) return null;
  if (React.isValidElement(icon)) return icon;
  if (typeof icon === 'object' && icon !== null && icon.$$typeof) return React.createElement(icon);
  return icon;
};

const getButtonDiameter = (sizeType) => {
  switch (sizeType) {
    case 'table':
    case 'small':
      return sizes.button.small;
    case 'footer':
    case 'large':
      return sizes.button.large;
    default:
      return sizes.button.medium;
  }
};

const getIconFontSize = (sizeType) => {
  switch (sizeType) {
    case 'table':
    case 'small':
      return sizes.icon.small;
    case 'footer':
    case 'large':
      return sizes.icon.large;
    default:
      return sizes.icon.medium;
  }
};

const AppIconButton = ({
  icon,
  onClick,
  tooltip = '',
  color,
  disabled = false,
  size = 'medium',
  sizeType = 'medium',
  variant = 'outlined',
  sx,
  ...props
}) => {
  const IconComponent = useMemo(() => resolveIcon(icon), [icon]);
  const diameter = useMemo(() => getButtonDiameter(sizeType), [sizeType]);
  const iconFontSize = useMemo(() => getIconFontSize(sizeType), [sizeType]);
  const iconButtonGap = layoutSpacing.iconGap;

  const sharedSizeStyles = useMemo(
    () => ({
      width: diameter,
      height: diameter,
      minWidth: diameter,
      minHeight: diameter,
      borderRadius: borderRadius.full,
      '& .MuiSvgIcon-root': { fontSize: iconFontSize },
    }),
    [diameter, iconFontSize],
  );

  const iconOnlyStyles = useMemo(
    () => ({
      // ...sharedSizeStyles,
      padding: 0,
      transition: 'all 0.2s ease',
      '&:hover:not(:disabled)': {
        backgroundColor: 'rgba(0, 0, 0, 0)',
        borderRadius: borderRadius.xs,
      },
      '& .MuiSvgIcon-root': {
        ...sharedSizeStyles['& .MuiSvgIcon-root'],
        color: disabled ? 'action.disabled' : color ? `${color}.main` : 'rgba(0, 0, 0, 0.6)',
        transition: 'color 0.2s ease-in-out',
      },
      '&.Mui-disabled': {
        opacity: 0.6,
        '& .MuiSvgIcon-root': { color: 'action.disabled' },
      },
    }),
    [color, disabled, sharedSizeStyles, iconButtonGap],
  );

  const outlinedStyles = useMemo(() => {
    const activeColor = color || 'primary';
    return {
      ...sharedSizeStyles,
      borderRadius: borderRadius.xs,
      border: '1px solid',
      borderColor: disabled ? 'action.disabled' : `${activeColor}.main`,
      transition: 'all 0.2s ease',
      backgroundColor: disabled ? 'action.disabled' : 'white',
      color: disabled ? 'action.disabled' : `${activeColor}.main`,
      '&:hover:not(:disabled)': {
        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
        transform: 'translateY(-1px)',
        backgroundColor: `${activeColor}.50`,
      },
      '& .MuiSvgIcon-root': {
        // Spread the shared rule first: re-declaring this key would otherwise
        // drop the sizeType-driven fontSize and fall back to MUI's 24px default.
        ...sharedSizeStyles['& .MuiSvgIcon-root'],
        color: disabled ? 'action.disabled' : `${activeColor}.main`,
      },
      '&.Mui-disabled': {
        borderColor: 'action.disabled',
        color: 'action.disabled',
        opacity: 0.6,
      },
    };
  }, [color, disabled, sharedSizeStyles]);

  const containedStyles = useMemo(() => {
    const activeColor = color || 'primary';
    return {
      ...sharedSizeStyles,
      borderRadius: borderRadius.xs,
      transition: 'all 0.2s ease',
      backgroundColor: disabled ? 'rgba(26, 26, 26, 0.12)' : `${activeColor}.main`,
      color: disabled ? 'action.disabled' : 'white',
      '& .MuiSvgIcon-root': {
        // See the note in outlinedStyles — spread first so fontSize survives.
        ...sharedSizeStyles['& .MuiSvgIcon-root'],
        color: disabled ? 'action.disabled' : 'white',
      },
      '&:hover:not(:disabled)': {
        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
        transform: 'translateY(-1px)',
        backgroundColor: `${activeColor}.dark`,
      },
      '&.Mui-disabled': {
        opacity: 0.6,
        backgroundColor: 'rgba(26, 26, 26, 0.12) !important',
        '& .MuiSvgIcon-root': { color: 'action.disabled' },
      },
    };
  }, [color, disabled, sharedSizeStyles]);

  const buttonStyles = useMemo(() => {
    const base =
      variant === 'iconOnly'
        ? iconOnlyStyles
        : variant === 'contained'
          ? containedStyles
          : outlinedStyles;
    return sx ? [base, sx] : base;
  }, [variant, iconOnlyStyles, containedStyles, outlinedStyles, sx]);

  const button = (
    <MuiIconButton
      onClick={onClick}
      color={color}
      disabled={disabled}
      size={size}
      sx={buttonStyles}
      {...props}
    >
      {IconComponent}
    </MuiIconButton>
  );

  const wrapperStyle =
    variant === 'iconOnly' ? { marginInline: iconButtonGap / 2 } : undefined;

  return (
    <AppToolTip title={tooltip}>
      <span style={wrapperStyle}>{button}</span>
    </AppToolTip>
  );
};

AppIconButton.propTypes = {
  icon: PropTypes.oneOfType([PropTypes.element, PropTypes.elementType]),
  onClick: PropTypes.func,
  tooltip: PropTypes.node,
  color: PropTypes.string,
  disabled: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  sizeType: PropTypes.oneOf(['small', 'medium', 'large', 'table', 'footer']),
  variant: PropTypes.oneOf(['outlined', 'contained', 'iconOnly']),
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

export default memo(AppIconButton);
